import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import {
  EAnnouncementDisplayType,
  EAnnouncementType,
  IAnnouncement,
  TAnnouncementSnoozeStorage,
} from 'src/app/shared/model/announcement/announcement.model';
import { AnnouncementsModalComponent } from 'src/app/standalone/modals/announcements-modal/announcements-modal.component';

@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  private static readonly SNOOZE_STORAGE_KEY: string = 'announcementSnooze';
  private static readonly TIMESTAMP_FORMAT: string = 'YYYY-MM-DD HH:mm:ss';
  private openModalRef: NgbModalRef | null = null;

  constructor(
    private readonly toast: ToastrService,
    private readonly ngbModal: NgbModal,
  ) {}

  public display(announcements: IAnnouncement[]): void {
    if (!announcements?.length) {
      return;
    }

    const announcementsToShow: IAnnouncement[] = announcements.filter(
      (announcement: IAnnouncement) => !this.isSnoozed(announcement),
    );

    if (!announcementsToShow.length) {
      return;
    }

    const toastAnnouncements: IAnnouncement[] = announcementsToShow.filter(
      (announcement: IAnnouncement) => announcement.displayType === EAnnouncementDisplayType.TOAST,
    );
    const modalAnnouncements: IAnnouncement[] = announcementsToShow.filter(
      (announcement: IAnnouncement) => announcement.displayType === EAnnouncementDisplayType.MODAL,
    );

    toastAnnouncements.forEach((announcement: IAnnouncement) => this.showToast(announcement));

    if (modalAnnouncements.length) {
      this.showModal(modalAnnouncements);
    }

    this.updateSnoozeStorage(announcements, announcementsToShow);
  }

  private isSnoozed(announcement: IAnnouncement): boolean {
    if (announcement.snoozeMinutes === null) {
      return false;
    }

    const snoozedAt: string | undefined = this.readSnoozeStorage()[announcement.id];

    if (!snoozedAt) {
      return false;
    }

    const elapsedMinutes: number = moment().diff(
      moment(snoozedAt, AnnouncementService.TIMESTAMP_FORMAT),
      'minutes',
    );

    return elapsedMinutes < announcement.snoozeMinutes;
  }

  private updateSnoozeStorage(allAnnouncements: IAnnouncement[], shownAnnouncements: IAnnouncement[]): void {
    const now: string = moment().format(AnnouncementService.TIMESTAMP_FORMAT);
    const currentAnnouncementIds: number[] = allAnnouncements.map((announcement: IAnnouncement) => announcement.id);
    const storage: TAnnouncementSnoozeStorage = {};

    Object.entries(this.readSnoozeStorage()).forEach(([id, timestamp]: [string, string]) => {
      if (currentAnnouncementIds.includes(Number(id))) {
        storage[Number(id)] = timestamp;
      }
    });

    shownAnnouncements
      .filter((announcement: IAnnouncement) => announcement.snoozeMinutes !== null)
      .forEach((announcement: IAnnouncement) => (storage[announcement.id] = now));

    localStorage.setItem(AnnouncementService.SNOOZE_STORAGE_KEY, JSON.stringify(storage));
  }

  private readSnoozeStorage(): TAnnouncementSnoozeStorage {
    const raw: string | null = localStorage.getItem(AnnouncementService.SNOOZE_STORAGE_KEY);

    if (!raw) {
      return {};
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as TAnnouncementSnoozeStorage) : {};
    } catch {
      return {};
    }
  }

  private showToast(announcement: IAnnouncement): void {
    const disableTimeOut: boolean = announcement.durationSeconds === null;
    const timeOut: number = disableTimeOut ? 0 : announcement.durationSeconds * 1000;

    this.toast[this.toastSeverityByType(announcement.type)](announcement.message, '', {
      closeButton: true,
      progressBar: !disableTimeOut,
      disableTimeOut,
      timeOut,
      positionClass: 'toast-bottom-right',
    });
  }

  private showModal(announcements: IAnnouncement[]): void {
    if (this.openModalRef) {
      return;
    }

    this.openModalRef = this.ngbModal.open(AnnouncementsModalComponent, AnnouncementsModalComponent.options);
    this.openModalRef.componentInstance.announcements = announcements;
    this.openModalRef.componentInstance.modal = this.openModalRef;
    this.openModalRef.result.finally(() => (this.openModalRef = null));
  }

  private toastSeverityByType(type: EAnnouncementType): 'info' | 'warning' | 'error' {
    switch (type) {
      case EAnnouncementType.ALERT:
        return 'error';
      case EAnnouncementType.WARNING:
        return 'warning';
      default:
        return 'info';
    }
  }
}
